import { Controller } from "@hotwired/stimulus"

// Choose up to `limit` options, keep the order they were chosen in, and show
// the chosen ones above the rest. Each option supplies its own rank copy, so
// this controller never knows what is being picked.
//
// Usage:
//   <div data-controller="ranked-selection" data-ranked-selection-limit-value="2">
//     <div data-ranked-selection-target="chosen"></div>
//     <div data-ranked-selection-target="divider" hidden>…</div>
//     <div data-ranked-selection-target="pool">
//       <label data-ranked-selection-target="option"
//              data-name="Mr Ellis"
//              data-rank-labels='["We&#39;ll tell them first","If %{name} can&#39;t come"]'>
//         <input type="checkbox" …>
//         <span data-rank-label hidden></span>
//
// A rank label may interpolate %{name} from the option chosen before it.
export default class extends Controller {
  static targets = ["option", "chosen", "pool", "divider", "submit"]
  static values = { limit: { type: Number, default: 2 } }

  connect() {
    this.chosen = []
    this.sync()
  }

  // Delegated, so it survives options being moved between the two containers.
  change(event) {
    const option = event.target.closest("[data-ranked-selection-target='option']")
    if (!option) return

    if (this.checkbox(option).checked) {
      this.chosen.push(option)
    } else {
      this.chosen = this.chosen.filter((chosen) => chosen !== option)
    }

    this.sync()
  }

  sync() {
    this.chosen.forEach((option, index) => {
      this.move(option, this.chosenTarget)
      this.setRank(option, this.labelTemplate(option, index))
    })

    this.optionTargets
      .filter((option) => !this.chosen.includes(option))
      .forEach((option) => {
        this.move(option, this.poolTarget)
        this.setRank(option, "")
        this.checkbox(option).disabled = this.chosen.length >= this.limitValue
      })

    // An empty container is still a flex item, so leaving one on the page
    // spends a gap on nothing.
    this.chosenTarget.hidden = this.chosen.length === 0
    this.poolTarget.hidden = this.poolTarget.children.length === 0

    if (this.hasDividerTarget) {
      this.dividerTarget.hidden = this.chosenTarget.hidden || this.poolTarget.hidden
    }

    if (this.hasSubmitTarget) {
      this.gate(this.chosen.length === this.limitValue)
    }

    this.dispatch("sorted", { target: this.poolTarget })
  }

  // ui/button conveys its disabled state with aria-disabled, so both move together.
  gate(enabled) {
    this.submitTarget.disabled = !enabled

    if (enabled) {
      this.submitTarget.removeAttribute("aria-disabled")
    } else {
      this.submitTarget.setAttribute("aria-disabled", "true")
    }
  }

  // Re-appending a node that is already in place still detaches and reinserts it.
  move(option, container) {
    if (option.parentElement !== container) container.appendChild(option)
  }

  setRank(option, label) {
    const slot = this.rankSlot(option)
    if (!slot) return

    slot.textContent = label
    slot.hidden = !label
  }

  // %{name} refers to the option chosen immediately before this one.
  labelTemplate(option, index) {
    const template = this.rankLabels(option)[index] || ""
    const previous = this.chosen[index - 1]

    return previous ? template.replace("%{name}", previous.dataset.name || "") : template
  }

  rankLabels(option) {
    try {
      return JSON.parse(option.dataset.rankLabels || "[]")
    } catch {
      return []
    }
  }

  rankSlot(option) {
    return option.querySelector("[data-rank-label]")
  }

  checkbox(option) {
    return option.querySelector("input[type='checkbox']")
  }
}
